import type { JSX } from "react";
// Settings → Integrations.
//
// Page state:
//   - Top: list of every integration *row* (instances), dispatched to a
//     code-keyed React card component or a generic fallback.
//   - "+ Add integration" opens a marketplace modal — fast FE-only search
//     across INTEGRATION_CATALOG. Single-cardinality entries that already
//     have a row are greyed out.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INTEGRATION_CATALOG,
  instanceTag,
  type CatalogCode,
  type CatalogEntry,
} from "@finance-os/shared-types/integrations";
import { api, type IntegrationLogEntry, type IntegrationPublic } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { cn } from "../lib/utils.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { getIntegrationCard } from "../integrations/registry.js";
import { formatDuration, logStatusColor } from "../integrations/logFormat.js";

export function SettingsIntegrations(): JSX.Element {
  const [showMarketplace, setShowMarketplace] = useState(false);
  const q = useQuery({ queryKey: ["integrations"], queryFn: () => api.integrations() });

  return (
    <>
      <PageHeader title="Integrations">
        <Button
          onClick={() => {
            setShowMarketplace(true);
          }}
        >
          + Add integration
        </Button>
      </PageHeader>

      {q.isLoading && <div className="text-muted-foreground text-sm">Loading…</div>}
      {q.isError && <div className="text-bad text-sm">Failed to load integrations.</div>}

      {q.data?.items.length === 0 && (
        <Card className="gap-2 p-5">
          <div className="font-medium">No integrations yet</div>
          <div className="text-muted-foreground text-sm">
            Click <strong>+ Add integration</strong> to pick from the catalog.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-4">
        {q.data?.items.map((row) => (
          <IntegrationCardSwitch key={row.id} row={row} />
        ))}
      </div>

      <Modal
        open={showMarketplace}
        onClose={() => {
          setShowMarketplace(false);
        }}
        title="Add integration"
      >
        <Marketplace
          existing={q.data?.items ?? []}
          onClose={() => {
            setShowMarketplace(false);
          }}
        />
      </Modal>
    </>
  );
}

// ---------- list dispatch ----------

function IntegrationCardSwitch({ row }: { row: IntegrationPublic }): JSX.Element {
  const qc = useQueryClient();
  const onChanged = () => {
    void qc.invalidateQueries({ queryKey: ["integrations"] });
  };
  const Card = getIntegrationCard(row.code);
  if (Card) return <Card row={row} onChanged={onChanged} />;
  return <UnknownIntegrationCard row={row} onChanged={onChanged} />;
}

function renderSettingValueSummary(s: { type: string; value: unknown; has_value: boolean }): string {
  if (s.type === "encrypted_string") return s.has_value ? "••••••••" : "not set";
  if (s.type === "account_ids") {
    const len = Array.isArray(s.value) ? s.value.length : 0;
    return len === 0 ? "none linked" : `${String(len)} linked`;
  }
  if (s.value === null || s.value === undefined || s.value === "") return "not set";
  if (typeof s.value === "string" || typeof s.value === "number" || typeof s.value === "boolean")
    return String(s.value);
  return JSON.stringify(s.value);
}

// One config line in the card's Configuration summary.
interface ConfigRow {
  label: string;
  display: string;
  set: boolean;
}

function UnknownIntegrationCard({
  row,
  onChanged,
}: {
  row: IntegrationPublic;
  onChanged: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);
  const entry: CatalogEntry | undefined = (INTEGRATION_CATALOG as Record<string, CatalogEntry>)[row.code];
  // All integration config lives in the settings catalog. Single-cardinality
  // integrations share one global set (tag `integration:<code>`); multi-cardinality
  // ones have per-instance settings (tag `integration_instance:<id>`).
  const settingsTag = entry?.cardinality === "multi" ? instanceTag(row.id) : `integration:${row.code}`;
  const settings = useQuery({
    queryKey: ["settings", settingsTag],
    queryFn: () => api.settings({ tag: settingsTag }),
  });
  // Poll the log every 2s while any entry is in `running` state; stop otherwise.
  const log = useQuery({
    queryKey: ["integration-log", row.id],
    queryFn: () => api.integrationLog(row.id, 5),
    refetchInterval: (q) => {
      const items = q.state.data?.items ?? [];
      return items.some((e) => e.status === "running") ? 2000 : false;
    },
  });
  const toggle = useMutation({
    mutationFn: () => api.integrationUpdate(row.id, { enabled: !row.enabled }),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => api.integrationDelete(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
  const test = useMutation({
    mutationFn: () => api.integrationTest(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integration-log", row.id] });
    },
  });
  const sync = useMutation({
    mutationFn: () => api.integrationSync(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integration-log", row.id] });
      void qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
  const canDelete = entry?.cardinality === "multi";

  const displayName = row.label && row.label !== row.title ? `${row.title} — ${row.label}` : row.title;
  const tagged = settings.data?.items ?? [];
  // Every integration configures through the catalog now, so a Configure button
  // shows whenever there are config fields to edit.
  const hasCatalogConfig = tagged.length > 0;
  const configRows: ConfigRow[] = tagged.map((s) => ({
    label: s.title,
    display: renderSettingValueSummary(s),
    set: s.has_value,
  }));
  const allSet = configRows.length > 0 && configRows.every((r) => r.set);
  const someSet = configRows.some((r) => r.set);
  // Inbound (push) integrations have no on-demand sync — hide "Sync now".
  const canSync = entry?.direction !== "push";
  const entries = log.data?.items ?? [];
  const lastEntry = entries[0];

  return (
    <Card className="gap-3 p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">{displayName}</div>
        {entry?.n8nWorkflowUrl && (
          <a
            href={entry.n8nWorkflowUrl}
            target="_blank"
            rel="noreferrer"
            title="Open the n8n workflow that drives this integration"
            className="text-muted-foreground text-[13px] whitespace-nowrap hover:no-underline"
          >
            n8n workflow ↗
          </a>
        )}
      </div>
      <div className="text-muted-foreground text-sm">{row.description}</div>

      {configRows.length > 0 && (
        <div className="bg-muted rounded-md p-3">
          <div className="text-muted-foreground mb-2 text-[11px] font-medium tracking-wide uppercase">
            Configuration ({allSet ? "all set" : someSet ? "partial" : "not configured"})
          </div>
          <div className="flex flex-col gap-1">
            {configRows.map((r) => (
              <div key={r.label} className="flex justify-between gap-2 text-sm">
                <span className="text-muted-foreground">{r.label}</span>
                <span className={r.set ? "text-foreground" : "text-muted-foreground"}>
                  {r.display}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-muted-foreground text-sm">
        Status: {row.enabled ? "enabled" : "disabled"}
        {row.last_synced_at && <> · last synced {new Date(row.last_synced_at).toLocaleString()}</>}
        {row.last_sync_error && (
          <>
            {" "}
            · <span className="text-bad">error: {row.last_sync_error}</span>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {hasCatalogConfig && (
          <Button asChild size="sm">
            <Link to={`/settings/catalog?tag=${encodeURIComponent(settingsTag)}`}>Configure →</Link>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            toggle.mutate();
          }}
          disabled={toggle.isPending}
        >
          {row.enabled ? "Disable" : "Enable"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            test.mutate();
          }}
          disabled={test.isPending || sync.isPending || !row.enabled}
        >
          {test.isPending ? "Testing…" : "Test"}
        </Button>
        {canSync && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              sync.mutate();
            }}
            disabled={test.isPending || sync.isPending || !row.enabled}
          >
            {sync.isPending ? "Syncing…" : "Sync now"}
          </Button>
        )}
        {canDelete && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (confirm(`Remove ${displayName}?`)) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            Remove
          </Button>
        )}
      </div>

      <div className="border-t pt-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              setLogOpen((v) => !v);
            }}
            className="text-muted-foreground cursor-pointer text-xs"
          >
            {logOpen ? "▾" : "▸"} Log ({entries.length}
            {lastEntry ? ` · last ${lastEntry.status}` : ""})
          </button>
          <Link
            to={`/data/logs?integration=${encodeURIComponent(row.id)}`}
            className="text-muted-foreground text-xs"
          >
            View all logs →
          </Link>
        </div>
        {logOpen && (
          <div className="mt-2 flex flex-col gap-1.5">
            {entries.length === 0 && (
              <div className="text-muted-foreground text-sm">No log entries yet.</div>
            )}
            {entries.map((e) => (
              <LogRow key={e.id} entry={e} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function LogRow({ entry }: { entry: IntegrationLogEntry }): JSX.Element {
  const [open, setOpen] = useState(false);
  const durationStr = formatDuration(entry.duration_ms);
  return (
    <div className="bg-muted rounded-md p-2 text-xs">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="text-foreground w-full cursor-pointer text-left"
      >
        <div className="flex items-center gap-2">
          <span className={cn("min-w-14 font-semibold", logStatusColor(entry.status))}>
            {entry.status}
          </span>
          <span className="text-muted-foreground">{entry.kind}</span>
          <span className="text-muted-foreground">
            {new Date(entry.started_at).toLocaleString()}
          </span>
          <span className="text-muted-foreground">{durationStr}</span>
          {entry.updated !== null && (
            <span className="text-muted-foreground">upd {String(entry.updated)}</span>
          )}
          <span className="text-muted-foreground flex-1 truncate">
            {entry.note ?? entry.error ?? ""}
          </span>
        </div>
      </button>
      {open && (
        <pre className="text-muted-foreground m-0 mt-2 overflow-auto text-[11px]">
          {JSON.stringify(
            {
              note: entry.note,
              error: entry.error,
              triggered_by: entry.triggered_by,
              actor: entry.actor,
              data: entry.data,
            },
            null,
            2,
          )}
        </pre>
      )}
    </div>
  );
}

// ---------- marketplace ----------

interface MarketplaceProps {
  existing: IntegrationPublic[];
  onClose: () => void;
}

function Marketplace({ existing, onClose }: MarketplaceProps): JSX.Element {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<CatalogCode | null>(null);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  // FE-only fuzzy filter: case-insensitive substring across title + description + code.
  const entries = useMemo(() => {
    const allEntries = Object.entries(INTEGRATION_CATALOG) as [CatalogCode, CatalogEntry][];
    const q = query.trim().toLowerCase();
    if (!q) return allEntries;
    return allEntries.filter(
      ([code, entry]) =>
        code.includes(q) ||
        entry.title.toLowerCase().includes(q) ||
        entry.description.toLowerCase().includes(q),
    );
  }, [query]);

  const create = useMutation({
    mutationFn: (input: { code: CatalogCode; label?: string }) => api.integrationCreate(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["integrations"] });
      onClose();
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  const handleAdd = (code: CatalogCode, entry: CatalogEntry) => {
    setError(null);
    if (entry.cardinality === "multi") {
      // Open the label sub-prompt within the same modal.
      setPending(code);
      setLabel("");
      return;
    }
    create.mutate({ code });
  };

  // Multi-cardinality label step.
  if (pending) {
    const entry = INTEGRATION_CATALOG[pending];
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!label.trim()) return;
          create.mutate({ code: pending, label: label.trim() });
        }}
      >
        <div className="text-muted-foreground mb-3 text-sm">
          Adding <strong>{entry.title}</strong>. Give this instance a label so you can tell it apart
          from others later.
        </div>
        <Field label="Label *">
          <TextInput
            required
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
            }}
            placeholder="Personal Drive"
            autoFocus
          />
        </Field>
        {error && <div className="text-bad mb-3 text-sm">{error}</div>}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setPending(null);
            }}
          >
            Back
          </Button>
          <Button type="submit" disabled={create.isPending || !label.trim()}>
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </div>
      </form>
    );
  }

  // Catalog list with search.
  const existingCodes = new Set(existing.map((row) => row.code));
  return (
    <>
      <Field label="Search">
        <TextInput
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder="Search by name or description…"
          autoFocus
        />
      </Field>
      <div className="mt-2 max-h-[400px] overflow-y-auto">
        {entries.length === 0 && <div className="text-muted-foreground p-4 text-sm">No matches.</div>}
        {entries.map(([code, entry]) => {
          const alreadyAdded = entry.cardinality === "single" && existingCodes.has(code);
          return (
            <div
              key={code}
              className={cn(
                "flex items-center gap-3 border-b py-3",
                alreadyAdded && "opacity-50",
              )}
            >
              <div className="w-7 text-2xl">{entry.icon ?? "🧩"}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{entry.title}</div>
                <div className="text-muted-foreground text-sm">{entry.description}</div>
                <div className="text-muted-foreground mt-1 text-[11px]">
                  {entry.cardinality === "multi" ? "Multiple instances allowed" : "Single instance only"}
                  {entry.category && ` · ${entry.category}`}
                </div>
              </div>
              <Button
                variant={alreadyAdded ? "outline" : "default"}
                size="sm"
                onClick={() => {
                  handleAdd(code, entry);
                }}
                disabled={alreadyAdded || create.isPending}
              >
                {alreadyAdded ? "Already added" : "Add"}
              </Button>
            </div>
          );
        })}
      </div>
      {error && <div className="text-bad mt-3 text-sm">{error}</div>}
    </>
  );
}
